// Copyright 2019 Sorama B.V.

import { Disposable } from "aurelia-binding";
import { Point2D } from "datatypes/SoramaWebApi";

export class GestureListener {
    private readonly zoomListeners: ((zoom: number) => number | void)[] = [];
    private readonly panListeners: ((offset: Point2D) => Point2D | void)[] = [];

    private mouseDown = false;

    private zoom: number;
    private pan: Point2D;

    constructor(
        private readonly element: HTMLElement,
        initialZoom = 1,
        initialPan: Point2D = { x: 0, y: 0 },
        private readonly zoomStep: number = 1.5
    ) {
        this.reset(initialZoom, initialPan);
    }

    public reset(zoom = 1, pan: Point2D = { x: 0, y: 0 }): void {
        this.setZoom(zoom);
        this.setPan(pan);
    }

    public zoomListener(onZoom: (zoom: number) => number | void): Disposable {
        const disposable = this.registerListener(onZoom, this.zoomListeners, [
            { event: "wheel", function: this.onScroll },
        ]);

        const update = <number>onZoom(this.zoom);
        if (update !== undefined && Math.abs(this.zoom - update) > 1e-5) {
            this.setZoom(update);
        }

        return disposable;
    }

    public panListener(onPan: (offset: Point2D) => Point2D | void): Disposable {
        const disposable = this.registerListener(onPan, this.panListeners, [
            { event: "mousedown", function: this.onMouseDown },
            { event: "mouseup", function: this.onMouseUp },
            { event: "mousemove", function: this.onMouseMove },
            { event: "contextmenu", function: this.onContextMenu },
            { event: "mouseleave", function: this.onMouseLeave },
        ]);

        const update = <Point2D>onPan(this.pan);
        if (
            update !== undefined &&
            (Math.abs(update.x - this.pan.x) > 1e-5 || Math.abs(update.y - this.pan.y) > 1e-5)
        ) {
            this.setPan(update);
        }

        return disposable;
    }

    private registerListener<T>(
        callback: T,
        list: T[],
        handlers: { event: string; function: EventListener }[]
    ): Disposable {
        list.push(callback);

        if (list.length === 1) {
            // Register listener on element
            for (const handler of handlers) {
                this.element.addEventListener(handler.event, handler.function);
            }
        }
        return {
            dispose: () => {
                const index = list.findIndex((x) => x === callback);
                if (index === -1) return;

                list.splice(index, 1);

                if (list.length === 0) {
                    // Remove listener on element
                    for (const handler of handlers) {
                        this.element.removeEventListener(handler.event, handler.function);
                    }
                }
            },
        };
    }

    private setZoom(zoom: number, pivotX?: number, pivotY?: number): void {
        // Remember original zoom
        const oldZoom = this.zoom;

        this.zoom = zoom;
        for (let i = 0; i < this.zoomListeners.length; i++) {
            const callback = this.zoomListeners[i];
            const zoomUpdate = <number>callback(this.zoom);

            if (zoomUpdate !== undefined && Math.abs(this.zoom - zoomUpdate) > 1e-5) {
                // Redo..
                this.zoom = zoomUpdate;
                i = -1;
            }
        }

        if (pivotX !== undefined && pivotY !== undefined) {
            // Get new zoom
            const newZoom = this.zoom;

            // Update pan to make sure that the pivot point stays the same
            this.setPan({
                x: -(((-this.pan.x + pivotX) / oldZoom) * newZoom - pivotX),
                y: -(((-this.pan.y + pivotY) / oldZoom) * newZoom - pivotY),
            });
        }
    }

    private setPan(pan: Point2D): void {
        this.pan = pan;

        for (let i = 0; i < this.panListeners.length; i++) {
            const callback = this.panListeners[i];
            const panUpdate = <Point2D>callback(this.pan);

            if (
                panUpdate !== undefined &&
                (Math.abs(panUpdate.x - this.pan.x) > 1e-5 || Math.abs(panUpdate.y - this.pan.y) > 1e-5)
            ) {
                // Redo..
                this.pan = panUpdate;
                i = -1;
            }
        }
    }

    //#region Mouse Handlers

    private onScroll = (mouseEvent: WheelEvent): void => {
        const zoomStep = mouseEvent.altKey ? (this.zoomStep - 1) * 0.1 + 1 : this.zoomStep;

        // Compute zoom
        this.setZoom(
            mouseEvent.deltaY > 0 ? this.zoom / zoomStep : this.zoom * zoomStep,
            mouseEvent.offsetX,
            mouseEvent.offsetY
        );
    };

    private onMouseMove = (mouseEvent: MouseEvent): void => {
        if (!this.mouseDown) {
            return;
        }

        const scale = mouseEvent.altKey ? 0.1 : 1;

        this.setPan({ x: this.pan.x + mouseEvent.movementX * scale, y: this.pan.y + mouseEvent.movementY * scale });
    };

    private onMouseDown = (mouseEvent: MouseEvent): void => {
        if (mouseEvent.button === 2) {
            this.mouseDown = true;
        }
    };

    private onMouseUp = (mouseEvent: MouseEvent): void => {
        if (mouseEvent.button === 2) {
            this.mouseDown = false;
        }
    };

    private onMouseLeave = (): void => {
        this.mouseDown = false;
    };

    private onContextMenu = (mouseEvent: MouseEvent): void => {
        mouseEvent.preventDefault();
    };

    //#endregion
}
